/**
 * Move between accounting and valuation without leaving the model.
 *
 * The separation is about keeping two kinds of work - and two very different
 * degrees of assurance - from being confused with each other. It is not about
 * making a user close a project and start again to see what it is worth. So the
 * disciplines are chosen first and shown apart, and this switches between them in
 * place, on the same model.
 *
 * A segmented control rather than a dropdown, because there are two options today
 * and four expected: at that size the choices should be readable without a click,
 * and the current one obvious without reading a label.
 *
 * Driven by `DISCIPLINES`, so the two to come need no change here.
 */

import { DISCIPLINES, type Discipline, type DisciplineId } from './disciplines';

export default function DisciplineSwitch({
  current,
  onSwitch,
}: {
  current: Discipline;
  onSwitch: (id: DisciplineId) => void;
}) {
  return (
    <div
      className="flex shrink-0 rounded-lg border border-gray-200 bg-gray-50 p-0.5"
      role="group"
      aria-label="Discipline"
    >
      {DISCIPLINES.map((discipline) => {
        const active = discipline.id === current.id;
        return (
          <button
            key={discipline.id}
            type="button"
            // Each option in its own discipline's colour, so the switch previews
            // where it leads.
            data-discipline={discipline.id}
            // The current one is not a button that does nothing: pressing it is a
            // no-op, and `aria-current` is what tells a screen reader which view
            // is showing.
            aria-current={active ? 'page' : undefined}
            onClick={() => !active && onSwitch(discipline.id)}
            className={[
              'rounded-md px-2.5 py-1 text-xs transition-colors',
              active
                ? 'bg-accent-600 font-semibold text-white shadow-sm'
                : 'text-gray-600 hover:bg-white hover:text-accent-700',
            ].join(' ')}
          >
            {discipline.label}
          </button>
        );
      })}
    </div>
  );
}
